// Tiny toast notification — a fixed-position banner that fades in at the top,
// auto-dismisses, used to confirm save success (or surface save errors) across
// admin pages. Pages instantiate the hook, render <ToastBanner /> once, then
// call show('message') after a successful save.
import { useCallback, useEffect, useRef, useState } from 'react'

export type ToastTone = 'success' | 'error'
interface ToastState { id: number; text: string; tone: ToastTone }

export function useToast() {
  const [toast, setToast] = useState<ToastState | null>(null)
  const timerRef = useRef<number | null>(null)

  const show = useCallback((text: string, tone: ToastTone = 'success', ms = 3000) => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    setToast({ id: Date.now(), text, tone })
    timerRef.current = window.setTimeout(() => setToast(null), ms)
  }, [])

  useEffect(() => () => { if (timerRef.current) window.clearTimeout(timerRef.current) }, [])

  return { toast, show }
}

export function ToastBanner({ toast }: { toast: ToastState | null }) {
  if (!toast) return null
  const ok = toast.tone === 'success'
  return (
    <div
      key={toast.id}
      className={'fixed top-6 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-lg shadow-xl border flex items-center gap-3 text-sm font-semibold pointer-events-none ' +
        (ok ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-red-50 border-red-300 text-red-900')}
      role="status"
      aria-live="polite"
    >
      <span className={'w-5 h-5 rounded-full flex items-center justify-center text-xs ' + (ok ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white')}>
        {ok ? '✓' : '!'}
      </span>
      <span>{toast.text}</span>
    </div>
  )
}
